import { useTranslation } from 'react-i18next';
import { TgsArticleImage } from '~/common/components/ArticleImage';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { PrinterIcon } from 'lucide-react';
import { ArticleIcons } from '~/features/catalog/components/article/ArticleIcons';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import { KeyFeatureList } from '~/features/catalog/components/articleDetail/KeyFeatureList';
import { NutritionList } from '~/features/catalog/components/articleDetail/NutritionList';
import { ProductInformationDownloadsAndLinks } from '~/features/catalog/components/articleDetail/ProductInformationDownloadsAndLinks';
import type { AdditionalInformationItem } from '~/features/catalog/components/articleDetail/AdditionalInformationList';
import AdditionalInformationList from '~/features/catalog/components/articleDetail/AdditionalInformationList';
import type { AghArticleDetail } from '~/common/types/aghArticleDetail';
import { useCatalogAghArticleDetail } from '~/features/catalog/hooks/useCatalogAghArticleDetail';
import { AddToTemplate } from '~/features/catalog/components/AddToTemplate';
import { useMemo } from 'react';
import { DownloadButton } from '~/common/components/DownloadButton';
import { useBasePath } from '~/common/hooks/useBasePath';
import type { ArticleInfoItem } from '~/common/components/ArticleInfoList';
import { ArticleInfoList } from '~/common/components/ArticleInfoList';
import { CatalogAghArticleNotAvailable } from '~/features/catalog/components/CatalogAghArticleNotAvailable';
import { useNavigate, useSearchParams } from 'react-router';
import type { ArticleChangeInfo } from '~/common/types/articleChangeInfo.ts';
import { PriceBadgeGroup } from '~/common/components/PriceBadgeGroup';
import { RecentOrder } from '~/features/catalog/components/articleDetail/RecentOrder.tsx';

export default function AghArticleDetailView({
  article,
  isAuthenticated,
  initialQuantity
}: Readonly<{ article: AghArticleDetail; isAuthenticated: boolean; initialQuantity?: number }>) {
  const { t } = useTranslation();
  const catalogArticle = useCatalogAghArticleDetail(article, initialQuantity);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang, path } = useBasePath();

  const handleNavigateToArticle = (changeInfo: ArticleChangeInfo) => {
    const searchTerm = searchParams.get('searchTerm');
    const articleDetailPath = `${path}/catalog/article/${changeInfo.articleNumber}`;
    const targetPath = searchTerm ? `${articleDetailPath}?searchTerm=${searchTerm}` : articleDetailPath;

    navigate(targetPath, {
      replace: true
    });
  };

  const items = useMemo(() => {
    const additionalInformationItems: AdditionalInformationItem[] = [
      {
        label: t('Wichtige Merkmale'),
        component: <KeyFeatureList article={catalogArticle} />
      }
    ];
    if (catalogArticle.showFactSheet && catalogArticle.nutritionFact) {
      additionalInformationItems.push({
        label: t('Nährwerte'),
        component: <NutritionList nutrition={catalogArticle.nutritionFact} />
      });
    }
    if (isAuthenticated) {
      additionalInformationItems.push({
        label: t('Dokumente und Links'),
        component: <ProductInformationDownloadsAndLinks article={article} />
      });
    }

    return additionalInformationItems;
  }, [article, catalogArticle, isAuthenticated, t]);

  const rohstoffHerkunftText = useMemo(() => {
    if (catalogArticle.rohstoffHerkunft && catalogArticle.rohstoffHerkunft.length > 0) {
      const maxItems = 3; // Nach Anforderung nur 3 anzeigen
      const selectedItems = catalogArticle.rohstoffHerkunft.slice(0, maxItems).map(x => x.text);
      const suffix = catalogArticle.rohstoffHerkunft.length > maxItems ? ' ...' : '';

      return selectedItems.join(', ') + suffix;
    }
    return '';
  }, [catalogArticle.rohstoffHerkunft]);

  const articleInfoItems: ArticleInfoItem[] = useMemo(
    () => [
      { label: t('Art. Nr.'), value: catalogArticle.articleNumber },
      { label: t('Marke'), value: catalogArticle.brand },
      { label: t('Rohstoffherkunft'), value: rohstoffHerkunftText }
    ],
    [catalogArticle.articleNumber, catalogArticle.brand, rohstoffHerkunftText, t]
  );

  const factSheetUrl = `/${lang}/resources/articles/${catalogArticle.articleNumber}/factsheet`;

  return (
    <div>
      <div className={'flex flex-col desktop:flex-row flex-wrap xl:flex-nowrap desktop:gap-8 mx-4 desktop:mx-0'}>
        <div className={'flex justify-center items-center w-full'}>
          <TgsArticleImage
            celumId={catalogArticle.celumId}
            articleNumber={catalogArticle.articleNumber}
            alt={catalogArticle.description}
            size={'large'}
          />
        </div>
        <div className={'mt-2 desktop:mt-0 w-full'}>
          <div className={'flex justify-between items-start'}>
            <ArticlePrice
              price={catalogArticle.price}
              oldPrice={catalogArticle.oldPrice}
              isAction={catalogArticle.isAction}
              actionValidFrom={catalogArticle.actionValidFrom}
              actionValidTo={catalogArticle.actionValidTo}
              unitText={catalogArticle.unitText}
              size={'lg'}
              showOnlyActionPrice={true}
              isHit={catalogArticle.isHit}
            />
            {isAuthenticated && (
              <DownloadButton
                name={`${catalogArticle.articleNumber}.pdf`}
                url={factSheetUrl}
                icon={<PrinterIcon className={'text-secondary'} />}
                data-testid={'download-button'}
              />
            )}
          </div>
          <PriceBadgeGroup
            direction="wrap"
            badges={[
              ...(catalogArticle.noveltyUntil ? [{ type: 'new' as const }] : []),
              ...(isAuthenticated && catalogArticle.isHit ? [{ type: 'hit' as const }] : [])
            ]}
          />
          <div className={'my-5'}>
            <div>
              <h1
                className={`text-3xl desktop:text-4xl ${catalogArticle.isAction && !catalogArticle.isHit && !catalogArticle.isFutureAction ? 'text-primary' : 'primary'}  py-3`}
                data-testid={'article-description'}
              >
                {catalogArticle.description}
              </h1>
              <ArticleInfoList items={articleInfoItems} />
            </div>
          </div>
          {catalogArticle.recentOrderDate && (
            <div className={'desktop:my-10'}>
              <RecentOrder article={article} isAgh={true} />
            </div>
          )}
          {catalogArticle.icons && catalogArticle.icons.length > 0 && (
            <div className={'my-4'}>
              <ArticleIcons icons={catalogArticle.icons} />
            </div>
          )}
          <ArticleSellUnit
            className={'desktop:mt-10'}
            selectedArticle={catalogArticle}
            allArticles={catalogArticle.allArticles}
            onArticleChange={
              changeInfo => setTimeout(() => handleNavigateToArticle(changeInfo), 300) // 300ms delay to avoid flickering
            }
            size={'lg'}
            showOnlyActionPrice={true}
          />
          <div className={'group desktop:mb-4'}>
            <AddToTemplate
              className={'min-w-[200px]'}
              buttonClassName={'h-auto desktop:h-[44px]'}
              mainArticleId={catalogArticle.mainArticleId}
              articleNumber={catalogArticle.articleNumber}
              articleDescriptionText={catalogArticle.description}
              isAuthenticated={isAuthenticated}
              quantity={catalogArticle.quantity}
              onQuantityChange={catalogArticle.setQuantity}
              calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
              variant={'default'}
              cacheQuantity={false}
              showLargeOnDesktop={true}
              forceSmallAddButton={false}
            />
            <CatalogAghArticleNotAvailable
              status={catalogArticle.status}
              showArticleNotAvailable={catalogArticle.showCurrentlyNotAvailableMessage}
              substituteArticleNumber={catalogArticle.substituteArticleNumber}
              substituteArticleText={catalogArticle.substituteArticleText}
            />
          </div>
        </div>
      </div>
      <div className={'desktop: mt-8'}>
        <AdditionalInformationList items={items} />
      </div>
    </div>
  );
}
